import type { PaletteColor } from "@trip/domain/palette";
import type { Asset } from "./api.ts";
import { localDayOf } from "./days.ts";

/**
 * Tags, as the library browses them.
 *
 * A tag is a label; filing several recordings under the same one is the grouping; and a tag
 * carrying a note is what the app calls an idea. The server decides what a folder holds and
 * what order folders come in — it is the only side that knows what this reader may see. What
 * is here is the part that is pure, and therefore the part worth testing: telling the Likes
 * sentinel from a real folder, cutting a note down to a card, and grouping a folder's
 * recordings into the days they were shot on.
 *
 * A tag's colour comes from the trip's one palette, the same eight a member's colour comes from
 * (`@trip/domain/palette`). The server picks it when the tag is made and stores it; nothing is
 * computed here, because a colour worked out from a tag's place in a list would move every time
 * somebody filed a recording.
 *
 * A folder is either the trip's or its owner's. Which it is, and whether this reader may change
 * it, are the server's to say: `canEdit` arrives decided rather than worked out here from an
 * owner id and a role list, because a client that works it out wrongly offers buttons the server
 * will refuse.
 */

export type Tag = {
  /** Null for `Likes`, which is not a tag but the recordings this reader hearted. */
  id: string | null;
  name: string;
  /** The note that makes a label an idea. */
  note: string | null;
  /**
   * One of the trip's eight, so a wall of folders can be told apart before it is read. Null
   * only for `Likes`: it is not a tag, and a colour there would read as a choice somebody made
   * rather than as the absence of one. It wears a heart instead.
   */
  color: PaletteColor | null;
  /** How many recordings this reader may see in it. */
  count: number;
  previewAssetIds: string[];
  /** Shared with the trip, or this reader's alone. `Likes` is always private. */
  visibility: "PUBLIC" | "PRIVATE";
  /** Whose folder it is. Null for `Likes`, which belongs to whoever is reading. */
  ownerId: string | null;
  /** Said in a name, because an id is not something a card can show. Null for `Likes`. */
  ownerName: string | null;
  /** Whether this reader may rename, repaint, note or delete it. */
  canEdit: boolean;
  /**
   * Whether it is this reader's own folder. A different question from `canEdit`: an admin may
   * correct a folder the trip shares without owning it, and only an owner may reshare one. True
   * for `Likes`, which is the reader's and is editable by nobody.
   */
  mine: boolean;
  /**
   * How many of the recordings in it somebody else filed — what the confirm dialog needs in
   * order to say what taking a folder private would cost other members.
   */
  othersFiled: number;
};

/**
 * `Likes` is the folder with no id. Checked on the id and never on the name, because a
 * traveller is perfectly entitled to make a real tag called "Likes" and it must not become
 * undeletable for having picked that word.
 */
export const isLikes = (t: Tag): boolean => t.id === null;

/**
 * A note cut to fit a folder card.
 *
 * Cut at a word so the tail is readable, and marked with an ellipsis so nobody mistakes the
 * fragment for the whole instruction — the full note is one tap away in the folder itself. A
 * note can be four thousand characters; a card is four lines.
 */
export function clampNote(note: string | null, max: number): string {
  if (!note) return "";
  const text = note.trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  // A single enormous word has no space to cut at, so it is cut mid-word rather than vanishing.
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export type DayGroup = {
  /** `YYYY-MM-DD` in the reader's own zone, or null for recordings with no time at all. */
  day: string | null;
  assets: Asset[];
};

/**
 * A folder's recordings, newest first, under the day each was shot on.
 *
 * A tag deliberately spans days — that is what makes it a tag rather than a date — so its grid
 * is one scroll with headers rather than the day tabs the Media tab uses. The day is the
 * reader's own local day, resolved the way the library resolves it everywhere else, so the two
 * screens can never disagree about which day a recording belongs to.
 *
 * Recordings with no time at all come last: they have no day to sit under, and dropping them
 * would make a folder's grid disagree with its own count.
 */
export function groupByDay(assets: Asset[], timeZone: string): DayGroup[] {
  const dated = assets
    .filter((a) => a.captureAt !== null && Number.isFinite(Date.parse(a.captureAt)))
    .sort((x, y) => Date.parse(y.captureAt!) - Date.parse(x.captureAt!));
  const undated = assets.filter(
    (a) => a.captureAt === null || !Number.isFinite(Date.parse(a.captureAt)),
  );

  const out: DayGroup[] = [];
  for (const a of dated) {
    const day = localDayOf(Date.parse(a.captureAt!), timeZone);
    const last = out[out.length - 1];
    if (last && last.day === day) last.assets.push(a);
    else out.push({ day, assets: [a] });
  }
  if (undated.length > 0) out.push({ day: null, assets: undated });
  return out;
}
